import { useRef, useEffect } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'

export default function EventSearch({
  searchAddress,
  searchRadius,
  sortBy,
  suggestions,
  showSuggestions,
  isSearching,
  onSearch,
  onAddressInput,
  onSuggestionClick,
  onRadiusChange,
  onSortChange,
  onResetLocation,
  distanceOptions,
  sortOptions
}) {
  const suggestionsRef = useRef(null)

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        onSuggestionClick(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onSuggestionClick])

  return (
    <div className="mb-8 card p-6">
      <form onSubmit={onSearch} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="input-label mb-1">
              Search by Address
            </label>
            <div className="relative" ref={suggestionsRef}>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchAddress}
                  onChange={onAddressInput}
                  placeholder="Enter an address..."
                  className="input-field pr-10"
                />
                <button
                  type="submit"
                  className="absolute inset-y-0 right-0 flex items-center pr-3
                    text-text-muted hover:text-text transition-colors"
                  disabled={isSearching}
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-dropdown w-full mt-1 rounded-lg shadow-lg
                  bg-bg-elevated border border-border overflow-hidden">
                  <ul className="max-h-60 py-1 overflow-auto">
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="cursor-pointer select-none py-3 px-4 min-h-[44px]
                          flex items-center
                          text-text-secondary hover:bg-primary-50 hover:text-text
                          transition-colors"
                        onClick={() => onSuggestionClick(suggestion)}
                      >
                        <span className="block truncate text-sm">
                          {suggestion.place_name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-4 sm:w-auto">
            <div className="w-full sm:w-48">
              <label htmlFor="radius" className="input-label mb-1">
                Distance
              </label>
              <select
                id="radius"
                value={searchRadius}
                onChange={(e) => onRadiusChange(Number(e.target.value))}
                className="input-field min-h-[44px]"
              >
                {distanceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-48">
              <label htmlFor="sort" className="input-label mb-1">
                Sort by
              </label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                className="input-field min-h-[44px]"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onResetLocation}
            className="text-sm font-medium text-primary-600 hover:text-primary-700
              min-h-[44px] px-2 transition-colors"
          >
            Use my current location
          </button>
        </div>
      </form>
    </div>
  )
}
